import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { Flex, Text } from 'core/components';
import { VirtualizedTable } from 'core/components/table';
import { Column } from 'core/components/table/table.types';
import { getMeasurementDimension } from '@kentik/ui-shared/query/measurements';
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import styled from 'styled-components';
import QueryRequest from 'app/stores/ude/QueryRequest';
import type RawEventModel from 'app/stores/ude/RawEventModel';
import { getDimensionRenderer } from 'app/views/boards/panels/ude/legendUtils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 100%;
`;

interface UdeEventsTableProps {
  queryRequest: QueryRequest;
}

function UdeEventsTable({ queryRequest }: UdeEventsTableProps) {
  const { query, rawRows, rawColumnKeys, rawEventsCollection } = queryRequest;
  const { measurement } = useDictionaryMeasurement(query.measurement);

  const columnKeys: string[] = useMemo(() => {
    if (rawColumnKeys.length > 0) {
      return rawColumnKeys;
    }
    if (rawRows.length > 0) {
      return Object.keys(rawRows[0]);
    }
    return [];
  }, [rawColumnKeys, rawRows]);

  const columns: Column<RawEventModel>[] = useMemo(
    () =>
      columnKeys.map((key) => {
        let label: string;
        if (!measurement) {
          const suffix = key.split('__').pop() ?? key;
          label = suffix.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        } else {
          const dimInfo = getMeasurementDimension(measurement, key);
          label = dimInfo?.Label ?? key.split('__').pop() ?? key;
        }

        const renderer = getDimensionRenderer({ metric: key, query });

        return {
          name: key,
          label,
          renderer: renderer
            ? ({ value, model }) =>
                renderer({
                  value: value != null ? String(value) : '',
                  model,
                  name: key
                })
            : undefined
        } as Column<RawEventModel>;
      }),
    [columnKeys, measurement, query]
  );

  if (!rawRows || rawRows.length === 0) {
    return (
      <Flex alignItems="center" justifyContent="center" p={3} flex={1}>
        <Text muted>No events found for the selected time range.</Text>
      </Flex>
    );
  }

  return (
    <Wrapper>
      <VirtualizedTable
        flexed
        collection={rawEventsCollection}
        columns={columns}
        rowHeight={30}
        selectOnRowClick={false}
      />
    </Wrapper>
  );
}

export default observer(UdeEventsTable);
